import { useCallback, useEffect, useRef, useState } from "react";

import api from "../../../services/api";
import { NOTIFICAME_API, buildSeamlessStartUrl } from "../connectionChannelsCatalog";

export default function useHubOAuth({ enabled }) {
	const [companyUuid, setCompanyUuid] = useState("");
	const [loading, setLoading] = useState(Boolean(enabled));
	const [status, setStatus] = useState("");
	const [success, setSuccess] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [activeFlow, setActiveFlow] = useState(null);
	const metaPopupRef = useRef(null);

	useEffect(() => {
		if (!enabled) return undefined;

		const loadSettings = async () => {
			setLoading(true);
			try {
				const { data } = await api.get("/settings");
				const current = data.find((s) => s.key === "notificameHub");
				setCompanyUuid(current?.value || "");
			} catch {
				setCompanyUuid("");
			} finally {
				setLoading(false);
			}
		};

		loadSettings();
	}, [enabled]);

	useEffect(() => {
		const listener = (event) => {
			if (event.origin !== NOTIFICAME_API) return;
			if (!event.data || !event.data.status) return;

			const payload = event.data;
			const message =
				typeof payload.message === "string"
					? payload.message
					: typeof payload.error === "string"
						? payload.error
						: "";

			if (payload.status === "channel-success") {
				setStatus("success");
				setSuccess(true);
				setErrorMessage("");
			} else {
				setStatus("error");
				setSuccess(false);
				setErrorMessage(message);
			}

			if (metaPopupRef.current && !metaPopupRef.current.closed) {
				metaPopupRef.current.close();
			}
		};

		window.addEventListener("message", listener);
		return () => window.removeEventListener("message", listener);
	}, []);

	const resetStatus = useCallback(() => {
		setStatus("");
		setSuccess(false);
		setErrorMessage("");
		setActiveFlow(null);
	}, []);

	const openMetaPopup = useCallback(
		(channel) => {
			if (!companyUuid) {
				setStatus("missingCompany");
				setSuccess(false);
				setErrorMessage("");
				return;
			}

			setActiveFlow(channel?.oauthProvider || channel?.oauthType || null);
			setErrorMessage("");
			setStatus("");
			setSuccess(false);

			const url = buildSeamlessStartUrl(channel, companyUuid, window.location.origin);

			metaPopupRef.current = window.open(
				url,
				"notificameMetaConnect",
				"width=800,height=900"
			);

			if (!metaPopupRef.current) {
				setStatus("popupBlocked");
				setSuccess(false);
				setErrorMessage("");
			}
		},
		[companyUuid]
	);

	return {
		companyUuid,
		hubTokenLoading: loading,
		status,
		success,
		errorMessage,
		activeFlow,
		openMetaPopup,
		resetStatus,
	};
}
